// ==========================================================
// Main Application & Game Loop Orchestrator (Paint Meister)
// ==========================================================

import { CanvasManager } from './engine/CanvasManager';
import { MasterEvaluator } from './engine/Evaluator';
import { STAGES, getLocalizedStage } from './stages/StageData';
import type { StageCategory } from './stages/StageData';
import { HUDController } from './ui/HUD';
import { ToolPaletteController } from './ui/ToolPalette';
import { ColorLabModalController } from './ui/ColorLabModal';
import { ResultModalController } from './ui/ResultModal';
import { ZoomWidgetController } from './ui/ZoomWidget';
import { TutorialModalController } from './ui/TutorialModal';
import { ShopModalController } from './ui/ShopModal';
import { TargetZoomModalController } from './ui/TargetZoomModal';
import { ScoreManager } from './engine/ScoreManager';
import { EconomyManager } from './engine/EconomyManager';
import { AdManager } from './engine/AdManager';
import { themeManager } from './engine/ThemeManager';
import { sounds } from './audio/SoundEffects';
import { i18n } from './i18n/I18nService';

export type GameMode = 'career' | 'colorlab' | 'free';

class PaintMeisterApp {
  private canvasMgr: CanvasManager;
  private hud: HUDController;
  private toolPalette: ToolPaletteController;
  private colorLabModal: ColorLabModalController;
  private resultModal: ResultModalController;
  private zoomWidget: ZoomWidgetController;
  private tutorialModal: TutorialModalController;
  private shopModal: ShopModalController;
  private targetZoomModal: TargetZoomModalController;

  // Game State
  private currentMode: GameMode = 'career';
  private currentCategory: StageCategory | 'all' = 'all';
  private currentStageIndex: number = 0;
  private isDrawing: boolean = false;
  private isPanning: boolean = false;
  private timerInterval: number | null = null;
  private elapsedSeconds: number = 0;
  private parTimeSeconds: number = 90;
  private lastEvaluationTime: number = 0;
  private colorMatchScore: number = 100;

  // Multi-Touch & Gesture Pointers Map
  private activePointers: Map<number, { clientX: number; clientY: number }> = new Map();
  private initialPinchDist: number | null = null;
  private lastPanPoint: { clientX: number; clientY: number } | null = null;

  // Title & Stage Select Elements
  private titleModalEl: HTMLElement;
  private stagesGridEl: HTMLElement;
  private btnTitleTheme: HTMLButtonElement;
  private btnTitleLang: HTMLButtonElement;
  private btnTitleTutorial: HTMLButtonElement;
  private btnTitleShop: HTMLButtonElement;

  constructor() {
    this.canvasMgr = new CanvasManager();
    this.hud = new HUDController();
    this.toolPalette = new ToolPaletteController();
    this.colorLabModal = new ColorLabModalController();
    this.resultModal = new ResultModalController();
    this.zoomWidget = new ZoomWidgetController(this.canvasMgr.viewport);
    this.tutorialModal = new TutorialModalController();
    this.shopModal = new ShopModalController();
    this.targetZoomModal = new TargetZoomModalController();

    this.titleModalEl = document.getElementById('modal-title-menu')!;
    this.stagesGridEl = document.getElementById('stages-select-grid')!;
    this.btnTitleTheme = document.getElementById('btn-title-theme') as HTMLButtonElement;
    this.btnTitleLang = document.getElementById('btn-title-lang') as HTMLButtonElement;
    this.btnTitleTutorial = document.getElementById('btn-title-tutorial') as HTMLButtonElement;
    this.btnTitleShop = document.getElementById('btn-title-shop') as HTMLButtonElement;

    this.initControllers();
    this.initPointerInteractions();
    this.initWheelInteractions();
    this.initTitleMenu();
    this.initCategoryTabs();
    this.initI18nSubscribers();

    // Populate initial stages grid & title UI
    AdManager.init();
    this.updateTitleLanguageUI();
    this.updateTitleThemeUI();
    this.renderStagesGrid();
    this.hud.updateCoins(EconomyManager.getCoins());

    themeManager.subscribe(() => {
      this.updateTitleThemeUI();
    });

    // Check first-time user tutorial prompt
    const hasSeenTutorial = localStorage.getItem('paint_meister_tutorial_seen');
    if (!hasSeenTutorial) {
      setTimeout(() => {
        this.tutorialModal.open('objective');
      }, 400);
    }
  }

  private initControllers() {
    // 1. HUD Callbacks
    this.hud.setCallbacks(
      () => this.finishWork(),
      () => this.showTitleMenu(),
      () => this.tutorialModal.open('objective'),
      () => this.shopModal.open(),
      (stage) => this.targetZoomModal.open(stage)
    );

    // 2. Tool Palette Callbacks
    this.toolPalette.setCallbacks({
      onToolChange: (tool) => {
        if (tool === 'hand') {
          this.zoomWidget.setHandModeUI(true);
        } else {
          this.zoomWidget.setHandModeUI(false);
          this.canvasMgr.tapeManager.setHoveredTape(null);
          this.canvasMgr.renderTapes();
        }
      },
      onColorChange: () => {
        // Color updated
      },
      onSizeChange: (size) => {
        this.canvasMgr.tapeManager.setTapeWidth(size * 1.2);
      },
      onPeelAllTapes: () => {
        this.peelAllTapesAction();
      },
      onUndo: () => {
        this.canvasMgr.undo();
        this.triggerRealtimeEvaluation();
      },
      onClear: () => {
        this.canvasMgr.clearPaint();
        this.triggerRealtimeEvaluation();
      },
      onOpenColorLab: () => {
        const stage = STAGES[this.currentStageIndex];
        const targetHex = stage.targetColors[0]?.hex || '#E74C3C';
        this.colorLabModal.open(targetHex, (hex, matchScore) => {
          this.toolPalette.addCustomColor(hex);
          this.colorMatchScore = matchScore;
        });
      }
    });

    // 3. Result Modal Callbacks
    this.resultModal.setCallbacks({
      onRetry: () => {
        this.startStage(this.currentStageIndex);
      },
      onNext: () => {
        let nextIndex = (this.currentStageIndex + 1) % STAGES.length;
        // Check if next stage is locked
        const nextStage = STAGES[nextIndex];
        if (!EconomyManager.isStageUnlocked(nextStage.id, nextStage.unlockCost)) {
          // If locked, go to menu
          this.showTitleMenu();
        } else {
          this.startStage(nextIndex);
        }
      },
      onExport: () => {
        this.exportArtwork();
      }
    });

    // 4. Tutorial Modal Start Callback
    this.tutorialModal.setOnStartCallback(() => {
      if (!this.titleModalEl.classList.contains('hidden')) {
        this.titleModalEl.classList.add('hidden');
        this.startStage(0);
      }
    });

    // 5. Shop Modal Callback
    this.shopModal.setOnBalanceUpdate(() => {
      this.updateTitleLanguageUI();
      this.renderStagesGrid();
      this.hud.updateCoins(EconomyManager.getCoins());
    });
  }

  private initTitleMenu() {
    // Mode tabs
    const modeTabs = document.querySelectorAll('.mode-tab');
    modeTabs.forEach(tab => {
      tab.addEventListener('click', () => {
        sounds.playClick();
        modeTabs.forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
        this.currentMode = tab.getAttribute('data-mode') as GameMode;
        this.renderStagesGrid();
      });
    });

    // Theme toggle in title screen
    this.btnTitleTheme?.addEventListener('click', () => {
      sounds.playClick();
      themeManager.toggleTheme();
    });

    // Language toggle in title screen
    this.btnTitleLang?.addEventListener('click', () => {
      sounds.playClick();
      i18n.toggleLanguage();
    });

    // Tutorial open button in title screen
    this.btnTitleTutorial?.addEventListener('click', () => {
      sounds.playClick();
      this.tutorialModal.open('objective');
    });

    // Shop open button in title screen
    this.btnTitleShop?.addEventListener('click', () => {
      sounds.playClick();
      this.shopModal.open();
    });
  }

  private updateTitleThemeUI() {
    const isDark = themeManager.getTheme() === 'dark';
    const themeIcon = document.getElementById('theme-title-icon');
    if (themeIcon) {
      themeIcon.textContent = isDark ? '☀️' : '🌙';
    }
  }

  private initCategoryTabs() {
    const catBtns = document.querySelectorAll('.cat-tab-btn');
    catBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        sounds.playClick();
        catBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.currentCategory = btn.getAttribute('data-cat') as (StageCategory | 'all');
        this.renderStagesGrid();
      });
    });
  }

  private initI18nSubscribers() {
    i18n.subscribe(() => {
      this.updateTitleLanguageUI();
      this.renderStagesGrid();
    });
  }

  /**
   * Localize the site-information footer, the shop's no-real-money notice and
   * the in-game house promo disclosure.
   */
  private updateLegalLanguageUI() {
    const legal = i18n.dict.legal;

    const linkTexts: Array<[string, string]> = [
      ['link-about', legal.about],
      ['link-contact', legal.contact],
      ['link-privacy', legal.privacy],
      ['link-terms', legal.terms]
    ];

    linkTexts.forEach(([id, text]) => {
      const el = document.getElementById(id);
      if (el) el.textContent = text;
    });

    const copyrightEl = document.querySelector('.site-copyright');
    if (copyrightEl) {
      copyrightEl.textContent = legal.copyright.replace('{year}', String(new Date().getFullYear()));
    }

    const shopNoticeEl = document.getElementById('shop-free-disclosure');
    if (shopNoticeEl) {
      const shop = i18n.dict.shop;
      shopNoticeEl.textContent = shop.freeNotice;
      const termsLink = document.createElement('a');
      termsLink.href = '/terms';
      termsLink.target = '_blank';
      termsLink.rel = 'noopener';
      termsLink.textContent = shop.freeNoticeTermsLink;
      shopNoticeEl.appendChild(termsLink);
      shopNoticeEl.appendChild(document.createTextNode(i18n.getLanguage() === 'ja' ? 'をご覧ください。' : '.'));
    }

    const houseBadgeEl = document.getElementById('ad-house-badge');
    if (houseBadgeEl) houseBadgeEl.textContent = legal.houseAdBadge;

    const houseDisclosureEl = document.getElementById('ad-house-disclosure');
    if (houseDisclosureEl) houseDisclosureEl.textContent = legal.houseAdDisclosure;
  }

  private updateTitleLanguageUI() {
    const t = i18n.dict.app;
    const lang = i18n.getLanguage();

    if (this.btnTitleLang) {
      this.btnTitleLang.textContent = lang === 'ja' ? '🇯🇵 JP' : '🇺🇸 EN';
    }

    const titleGuideSpan = document.querySelector('#btn-title-tutorial span');
    if (titleGuideSpan) {
      titleGuideSpan.textContent = lang === 'ja' ? '❓ 遊び方ガイド' : '❓ How to Play';
    }

    const titleShopSpan = document.querySelector('#btn-title-shop span');
    if (titleShopSpan) {
      titleShopSpan.textContent = t.shopBtn;
    }

    const tagEl = document.querySelector('.title-tag');
    if (tagEl) tagEl.textContent = t.tagline;

    const kanjiEl = document.querySelector('.kanji-title');
    if (kanjiEl) kanjiEl.textContent = t.title;

    const subKanjiEl = document.querySelector('.sub-kanji');
    if (subKanjiEl) subKanjiEl.textContent = t.subtitle;

    const descEl = document.querySelector('.title-desc');
    if (descEl) descEl.textContent = t.description;

    const tipEl = document.querySelector('.footer-tip-text');
    if (tipEl) tipEl.textContent = t.tip;

    this.updateLegalLanguageUI();

    // Mode tabs text
    const modeTabs = document.querySelectorAll('.mode-tab');
    if (modeTabs[0]) modeTabs[0].textContent = i18n.dict.modes.career;
    if (modeTabs[1]) modeTabs[1].textContent = i18n.dict.modes.colorlab;
    if (modeTabs[2]) modeTabs[2].textContent = i18n.dict.modes.free;

    // Category tabs text
    const catBtns = document.querySelectorAll('.cat-tab-btn');
    if (catBtns[0]) catBtns[0].textContent = t.allCategories;
    const cats = i18n.dict.categories;
    if (catBtns[1]) catBtns[1].textContent = cats.furniture;
    if (catBtns[2]) catBtns[2].textContent = cats.vehicles;
    if (catBtns[3]) catBtns[3].textContent = cats.music;
    if (catBtns[4]) catBtns[4].textContent = cats.gourmet;
    if (catBtns[5]) catBtns[5].textContent = cats.toys;
    if (catBtns[6]) catBtns[6].textContent = cats.crafts;
    if (catBtns[7]) catBtns[7].textContent = cats.scifi;
    if (catBtns[8]) catBtns[8].textContent = cats.animals;
    if (catBtns[9]) catBtns[9].textContent = cats.fashion;
    if (catBtns[10]) catBtns[10].textContent = cats.relics;
    if (catBtns[11]) catBtns[11].textContent = cats.landmarks;

    // Stats & Coins bar
    const coinsLabel = document.getElementById('title-coins-label');
    if (coinsLabel) coinsLabel.textContent = `${t.coinsLabel}:`;

    const coinsValEl = document.getElementById('title-coins-val');
    if (coinsValEl) coinsValEl.textContent = EconomyManager.getCoins().toLocaleString();

    const starsLabel = document.getElementById('title-stars-label');
    if (starsLabel) starsLabel.textContent = `${t.starsCollected}:`;

    const scoreLabel = document.getElementById('title-score-label');
    if (scoreLabel) scoreLabel.textContent = `${t.totalScoreLabel}:`;

    const totalStars = ScoreManager.getTotalStars();
    const totalScore = ScoreManager.getTotalScore();

    const starsValEl = document.getElementById('title-total-stars');
    if (starsValEl) starsValEl.textContent = `${totalStars} / ${STAGES.length * 3} ⭐`;

    const scoreValEl = document.getElementById('title-total-score');
    if (scoreValEl) scoreValVal(totalScore, scoreValEl);
  }

  private renderStagesGrid() {
    this.stagesGridEl.innerHTML = '';
    const lang = i18n.getLanguage();
    const allRecords = ScoreManager.getAllRecords();

    const filteredStages = STAGES.filter(stage => {
      if (this.currentCategory === 'all') return true;
      return stage.category === this.currentCategory;
    });

    filteredStages.forEach((stage) => {
      const idx = STAGES.findIndex(s => s.id === stage.id);
      const locStage = getLocalizedStage(stage, lang);
      const record = allRecords[stage.id];
      const isCleared = record && record.stars > 0;
      const isUnlocked = EconomyManager.isStageUnlocked(stage.id, stage.unlockCost);

      const card = document.createElement('div');
      card.className = `stage-card ${isCleared ? 'cleared' : ''} ${!isUnlocked ? 'locked' : ''}`;

      // Render star string
      let starsHtml = '';
      if (isCleared) {
        for (let s = 1; s <= 3; s++) {
          starsHtml += s <= record.stars ? '★' : '☆';
        }
      }

      card.innerHTML = `
        <span class="stage-card-icon">${isUnlocked ? locStage.icon : '🔒'}</span>
        <span class="stage-card-title">${locStage.name}</span>
        <span class="stage-card-diff">${lang === 'ja' ? '難易度' : 'Diff'}: ${locStage.difficulty}</span>
        ${isUnlocked ? (
          isCleared ? `
            <div class="stage-card-stars-row">${starsHtml}</div>
            <span class="stage-card-best-score">${record.highScore.toLocaleString()} pt</span>
          ` : `
            <span class="stage-card-stars-row" style="opacity: 0.4;">☆☆☆</span>
          `
        ) : `
          <span class="stage-card-lock-tag">🪙 ${stage.unlockCost.toLocaleString()}</span>
        `}
      `;

      card.addEventListener('click', () => {
        sounds.playClick();
        if (!isUnlocked) {
          // Unlock Stage Confirmation
          const confirmed = confirm(
            lang === 'ja'
              ? `『${locStage.name}』を 🪙 ${stage.unlockCost} コインで解放しますか？\n（現在の所持コイン: 🪙 ${EconomyManager.getCoins()}）`
              : `Unlock "${locStage.name}" for 🪙 ${stage.unlockCost} coins?\n(Current balance: 🪙 ${EconomyManager.getCoins()})`
          );
          if (confirmed) {
            const unlockRes = EconomyManager.unlockStage(stage.id, stage.unlockCost);
            if (unlockRes.success) {
              sounds.playFanfare(2);
              this.updateTitleLanguageUI();
              this.renderStagesGrid();
              this.hud.updateCoins(EconomyManager.getCoins());
            } else {
              alert(lang === 'ja' ? 'コインが不足しています！ショップで獲得できます。' : 'Not enough coins! You can get more in the Shop.');
              this.shopModal.open();
            }
          }
          return;
        }

        this.titleModalEl.classList.add('hidden');
        this.startStage(idx);
      });

      this.stagesGridEl.appendChild(card);
    });
  }

  private showTitleMenu() {
    this.stopTimer();
    this.updateTitleLanguageUI();
    this.renderStagesGrid();
    this.titleModalEl.classList.remove('hidden');
  }

  public startStage(stageIndex: number) {
    this.currentStageIndex = stageIndex;
    const stage = STAGES[stageIndex];
    this.parTimeSeconds = stage.parTimeSeconds;
    this.elapsedSeconds = 0;
    this.colorMatchScore = 100;

    // Reset Viewport to 100%
    this.canvasMgr.viewport.resetView();

    // Set Palette
    const paletteColors = stage.targetColors.map(c => c.hex);
    this.toolPalette.setPaletteColors(paletteColors);

    // Load in Canvas
    this.canvasMgr.loadStage(stage);
    this.canvasMgr.renderTapes();

    // Update HUD
    this.hud.setStage(stage);
    this.hud.updateMetrics(0, 0);
    this.hud.updateTimer(this.parTimeSeconds);
    this.hud.updateCoins(EconomyManager.getCoins());

    // Reset Tool to Brush
    this.toolPalette.setActiveTool('brush');
    this.zoomWidget.setHandModeUI(false);

    // Start Timer
    this.startTimer();
  }

  private startTimer() {
    this.stopTimer();
    this.timerInterval = window.setInterval(() => {
      this.elapsedSeconds++;
      const remaining = Math.max(0, this.parTimeSeconds - this.elapsedSeconds);
      this.hud.updateTimer(remaining);
    }, 1000);
  }

  private stopTimer() {
    if (this.timerInterval !== null) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  // --- Interaction & Touch / Pointer Pipeline with Zoom & Pan ---
  private initPointerInteractions() {
    const interactionLayer = this.canvasMgr.cursorCanvas;
    const stageEl = document.getElementById('canvas-stage');

    const onPointerDown = (e: PointerEvent) => {
      e.preventDefault();
      interactionLayer.setPointerCapture(e.pointerId);

      this.activePointers.set(e.pointerId, { clientX: e.clientX, clientY: e.clientY });

      // Multi-Touch Pinch & Two-Finger Pan Gesture
      if (this.activePointers.size === 2) {
        this.isDrawing = false;
        this.isPanning = true;
        this.canvasMgr.toolEngine.endStroke();
        sounds.stopContinuousSounds();

        const pts = Array.from(this.activePointers.values());
        this.initialPinchDist = Math.hypot(pts[0].clientX - pts[1].clientX, pts[0].clientY - pts[1].clientY);
        this.lastPanPoint = {
          clientX: (pts[0].clientX + pts[1].clientX) / 2,
          clientY: (pts[0].clientY + pts[1].clientY) / 2
        };
        return;
      }

      // Middle Click (Button 1) or Hand Tool Mode = Pan Drag
      const isMiddleClick = e.button === 1;
      const isHandTool = this.toolPalette.getActiveTool() === 'hand' || this.canvasMgr.viewport.isHandModeActive();

      if (isMiddleClick || isHandTool) {
        this.isPanning = true;
        this.isDrawing = false;
        this.lastPanPoint = { clientX: e.clientX, clientY: e.clientY };
        stageEl?.classList.add('panning');
        return;
      }

      // Single Pointer Drawing / Tape / Peel
      const pt = this.canvasMgr.getCanvasPoint(e.clientX, e.clientY);
      const tool = this.toolPalette.getActiveTool();
      const color = this.toolPalette.getActiveColor();
      const size = this.toolPalette.getActiveSize();

      if (tool === 'peel') {
        const tape = this.canvasMgr.tapeManager.findTapeAt(pt.x, pt.y);
        if (tape) {
          sounds.playTapePeel();
          this.canvasMgr.tapeManager.peelSingleTape(tape.id, () => {
            this.canvasMgr.renderTapes();
            this.triggerRealtimeEvaluation();
          });
          this.canvasMgr.renderTapes();
        }
        return;
      }

      this.isDrawing = true;
      this.isPanning = false;

      if (tool === 'tape') {
        this.canvasMgr.tapeManager.startTape(pt.x, pt.y);
        this.canvasMgr.renderTapes();
        sounds.playTapePlace();
      } else {
        this.canvasMgr.toolEngine.startStroke(pt.x, pt.y, e.pressure || 1.0);
        this.canvasMgr.toolEngine.drawStroke(
          this.canvasMgr.paintCtx,
          pt.x,
          pt.y,
          tool,
          color,
          size,
          this.canvasMgr.tapeManager.getTapes(),
          e.pressure || 1.0
        );

        if (tool === 'spray') {
          sounds.startSpray();
        } else if (tool === 'brush' || tool === 'roller') {
          sounds.startBrushStroke();
        }
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!this.activePointers.has(e.pointerId)) return;
      this.activePointers.set(e.pointerId, { clientX: e.clientX, clientY: e.clientY });

      // Multi-Touch Pinch Zoom & Pan Handler
      if (this.activePointers.size >= 2) {
        const pts = Array.from(this.activePointers.values());
        const currentDist = Math.hypot(pts[0].clientX - pts[1].clientX, pts[0].clientY - pts[1].clientY);
        const currentMid = {
          clientX: (pts[0].clientX + pts[1].clientX) / 2,
          clientY: (pts[0].clientY + pts[1].clientY) / 2
        };

        if (this.initialPinchDist && this.lastPanPoint) {
          const deltaScale = (currentDist - this.initialPinchDist) * 0.005;
          const dx = currentMid.clientX - this.lastPanPoint.clientX;
          const dy = currentMid.clientY - this.lastPanPoint.clientY;

          this.canvasMgr.viewport.zoomAt(deltaScale, currentMid.clientX, currentMid.clientY);
          this.canvasMgr.viewport.panBy(dx, dy);

          this.initialPinchDist = currentDist;
          this.lastPanPoint = currentMid;
        }
        return;
      }

      // Single Pointer Pan Handler
      if (this.isPanning && this.lastPanPoint) {
        const dx = e.clientX - this.lastPanPoint.clientX;
        const dy = e.clientY - this.lastPanPoint.clientY;
        this.canvasMgr.viewport.panBy(dx, dy);
        this.lastPanPoint = { clientX: e.clientX, clientY: e.clientY };
        return;
      }

      // Hover and Drawing Handler
      const pt = this.canvasMgr.getCanvasPoint(e.clientX, e.clientY);
      const tool = this.toolPalette.getActiveTool();
      const color = this.toolPalette.getActiveColor();
      const size = this.toolPalette.getActiveSize();

      // Render cursor preview
      if (tool !== 'hand') {
        this.canvasMgr.renderCursor(pt.x, pt.y, tool, size, color);
      } else {
        this.canvasMgr.clearCursor();
      }

      // Handle Tape Hover in Peel Tool mode
      if (tool === 'peel') {
        const tape = this.canvasMgr.tapeManager.findTapeAt(pt.x, pt.y);
        const prevHover = this.canvasMgr.tapeManager.getHoveredTape();
        const nextHover = tape ? tape.id : null;
        if (prevHover !== nextHover) {
          this.canvasMgr.tapeManager.setHoveredTape(nextHover);
          this.canvasMgr.renderTapes();
        }
        return;
      }

      if (!this.isDrawing) return;

      if (tool === 'tape') {
        this.canvasMgr.tapeManager.updateTape(pt.x, pt.y);
        this.canvasMgr.renderTapes();
      } else {
        this.canvasMgr.toolEngine.drawStroke(
          this.canvasMgr.paintCtx,
          pt.x,
          pt.y,
          tool,
          color,
          size,
          this.canvasMgr.tapeManager.getTapes(),
          e.pressure || 1.0
        );

        // Real-time audio modulation
        const speed = Math.hypot(e.movementX, e.movementY);
        sounds.updateBrushVelocity(speed);

        // Throttle realtime evaluation
        const now = performance.now();
        if (now - this.lastEvaluationTime > 250) {
          this.triggerRealtimeEvaluation();
          this.lastEvaluationTime = now;
        }
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      this.activePointers.delete(e.pointerId);

      if (this.activePointers.size === 0) {
        this.isPanning = false;
        this.lastPanPoint = null;
        this.initialPinchDist = null;
        stageEl?.classList.remove('panning');
      }

      if (!this.isDrawing) return;
      this.isDrawing = false;

      const pt = this.canvasMgr.getCanvasPoint(e.clientX, e.clientY);
      const tool = this.toolPalette.getActiveTool();

      if (tool === 'tape') {
        const tape = this.canvasMgr.tapeManager.finishTape(pt.x, pt.y);
        if (tape) {
          sounds.playTapePlace();
        }
        this.canvasMgr.renderTapes();
      } else if (tool !== 'peel') {
        this.canvasMgr.toolEngine.endStroke();
        sounds.stopContinuousSounds();
        this.canvasMgr.saveSnapshot();
        this.triggerRealtimeEvaluation();
      }
    };

    const onPointerCancel = (e: PointerEvent) => {
      this.activePointers.delete(e.pointerId);
      this.isDrawing = false;
      this.isPanning = false;
      this.lastPanPoint = null;
      this.initialPinchDist = null;
      stageEl?.classList.remove('panning');
      this.canvasMgr.toolEngine.endStroke();
      this.canvasMgr.tapeManager.cancelActiveTape();
      sounds.stopContinuousSounds();
      this.canvasMgr.clearCursor();
    };

    interactionLayer.addEventListener('pointerdown', onPointerDown);
    interactionLayer.addEventListener('pointermove', onPointerMove);
    interactionLayer.addEventListener('pointerup', onPointerUp);
    interactionLayer.addEventListener('pointercancel', onPointerCancel);
    interactionLayer.addEventListener('pointerleave', onPointerCancel);
  }

  private initWheelInteractions() {
    const wrapper = document.getElementById('atelier-canvas-wrapper');
    wrapper?.addEventListener('wheel', (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY < 0 ? 0.15 : -0.15;
      this.canvasMgr.viewport.zoomAt(delta, e.clientX, e.clientY);
    }, { passive: false });
  }

  private peelAllTapesAction() {
    sounds.playTapePeel();
    this.canvasMgr.tapeManager.peelAllTapes(() => {
      this.canvasMgr.renderTapes();
      this.triggerRealtimeEvaluation();
    });
  }

  private triggerRealtimeEvaluation() {
    const stage = STAGES[this.currentStageIndex];
    const result = MasterEvaluator.evaluate(
      this.canvasMgr.baseCanvas,
      this.canvasMgr.paintCanvas,
      this.elapsedSeconds,
      stage.parTimeSeconds,
      this.colorMatchScore
    );

    this.hud.updateMetrics(result.accuracyScore, result.finishScore);
  }

  private finishWork() {
    this.stopTimer();
    sounds.stopContinuousSounds();

    const stage = STAGES[this.currentStageIndex];
    const result = MasterEvaluator.evaluate(
      this.canvasMgr.baseCanvas,
      this.canvasMgr.paintCanvas,
      this.elapsedSeconds,
      stage.parTimeSeconds,
      this.colorMatchScore
    );

    // Calculate and add reward coins
    const earnedCoins = result.isPass
      ? EconomyManager.calculateStageReward(result.stars, result.totalScore)
      : 20;
    EconomyManager.addCoins(earnedCoins);
    this.hud.updateCoins(EconomyManager.getCoins());

    this.resultModal.show(stage.id, result, earnedCoins, this.currentMode === 'colorlab');
  }

  private exportArtwork() {
    const dataUrl = this.canvasMgr.exportCompositePNG();
    const link = document.createElement('a');
    link.download = `paint-meister-stage-${this.currentStageIndex + 1}.png`;
    link.href = dataUrl;
    link.click();
  }
}

function scoreValVal(score: number, el: HTMLElement) {
  el.textContent = `${score.toLocaleString()} pts`;
}

// Bootstrap on DOM Loaded
window.addEventListener('DOMContentLoaded', () => {
  new PaintMeisterApp();
});
